/**
 * OpenTelemetry browser tracing for redmap6 (ui6).
 *
 * Mirrors the shape of redmap.webapp.scp/packages/bff/src/otel/telemetry.ts
 * (single initTelemetry() entry point, export toggle, same resource attribute
 * naming) but uses the *web* SDK, because ui6 is a static SPA served by nginx
 * rather than a .NET/Node service.
 *
 * ## Where spans go
 *
 * The nginx container and the datadog-agent sidecar run in the SAME ECS task
 * (awsvpc => shared network namespace), and the agent's OTLP receiver is bound
 * to 0.0.0.0:4318. nginx reverse-proxies /v1/traces to 127.0.0.1:4318, so the
 * browser posts to its own origin:
 *
 *   Browser -> POST {origin}/v1/traces -> nginx :80 -> datadog-agent :4318 -> Datadog APM
 *
 * Same-origin means no CORS preflight, no publicly exposed collector, and no
 * Terraform change. See nginx/nginx.conf.
 *
 * ## Build constraint
 *
 * react-scripts 4.0.3 => webpack 4, which cannot parse optional chaining inside
 * node_modules. The Dockerfile Babel-transforms @opentelemetry/** for this
 * reason (same treatment react-resizable/react-draggable already get).
 *
 * DO NOT BUMP THE @opentelemetry VERSIONS without re-testing `docker build`.
 * They are pinned to 1.28.0 / 0.55.0 deliberately:
 *
 *   - webpack 4 ignores package "exports" maps, so it cannot resolve subpath
 *     imports like '@opentelemetry/otlp-exporter-base/browser-http'.
 *   - exporter-trace-otlp-http started importing exactly that subpath
 *     internally in 0.56.0, which fails the build with
 *     "Cannot find module: '@opentelemetry/otlp-exporter-base/browser-http'".
 *     0.55.0 is the newest release whose resolved tree has no such imports.
 *
 * For the same reason, do not import '@opentelemetry/semantic-conventions/incubating'.
 * Attribute keys below are plain string literals rather than imported constants.
 */

import {diag, DiagConsoleLogger, DiagLogLevel} from '@opentelemetry/api'
import {
  CompositePropagator,
  W3CBaggagePropagator,
  W3CTraceContextPropagator,
} from '@opentelemetry/core'
import {Resource} from '@opentelemetry/resources'
import {
  AlwaysOnSampler,
  BatchSpanProcessor,
  TraceIdRatioBasedSampler,
} from '@opentelemetry/sdk-trace-base'
import type {ReadableSpan, Sampler, SpanProcessor} from '@opentelemetry/sdk-trace-base'
import type {Span} from '@opentelemetry/api'
import {WebTracerProvider} from '@opentelemetry/sdk-trace-web'
import {OTLPTraceExporter} from '@opentelemetry/exporter-trace-otlp-http'
import {ZoneContextManager} from '@opentelemetry/context-zone'
import {registerInstrumentations} from '@opentelemetry/instrumentation'
import {DocumentLoadInstrumentation} from '@opentelemetry/instrumentation-document-load'
import {XMLHttpRequestInstrumentation} from '@opentelemetry/instrumentation-xml-http-request'
import {FetchInstrumentation} from '@opentelemetry/instrumentation-fetch'

/** Path nginx proxies to the datadog-agent sidecar. Must match nginx/nginx.conf. */
const TRACES_PATH = '/v1/traces'

export interface OtelSettings {
  serviceName: string
  serviceVersion: string
  environment: string
  sampleRatio: number
  endpoint: string
  propagateCors: boolean
  corsUrls: RegExp[]
}

/**
 * Resolve settings from the CRA build-time environment (aws/deploy.env).
 * Kept separate and exported so it can be unit tested without booting the SDK.
 */
export function resolveSettings(): OtelSettings {
  const env = process.env

  let sampleRatio = 1.0
  const rawRatio = env.REACT_APP_OTEL_SAMPLE_RATIO
  if (rawRatio) {
    const parsed = parseFloat(rawRatio)
    if (!isNaN(parsed)) {
      sampleRatio = Math.min(Math.max(parsed, 0), 1)
    }
  }

  return {
    // Environment-agnostic, per the unified service tagging convention:
    // 'redmap6', never 'redmap6-uat'.
    serviceName: env.REACT_APP_OTEL_SERVICE_NAME || 'redmap6',
    serviceVersion: env.REACT_APP_VERSION || '0.0.0',
    environment: (env.REACT_APP_ENVIRONMENT || 'unknown').trim(),
    sampleRatio,
    endpoint: `${window.location.origin}${TRACES_PATH}`,
    // Off by default: sending traceparent cross-origin triggers a CORS
    // preflight, and the API must answer with
    //   Access-Control-Allow-Headers: traceparent, tracestate, baggage
    // or the API CALL ITSELF fails. Enable per environment only once the
    // backends are confirmed to allow those headers.
    propagateCors: env.REACT_APP_OTEL_PROPAGATE_CORS === 'true',
    corsUrls: buildCorsUrls(),
  }
}

/** Origins we may attach trace context to, derived from the configured API URLs. */
function buildCorsUrls(): RegExp[] {
  const candidates = [
    process.env.REACT_APP_API_BASE_URL,
    process.env.REACT_APP_API_LIBRARY_URL,
    process.env.REACT_APP_API_METRICS_URL,
    process.env.REACT_APP_ID_AUTHORITY_URL,
  ]

  const origins: string[] = []
  candidates.forEach((value) => {
    if (!value) return
    try {
      const origin = new URL(value, window.location.origin).origin
      if (origins.indexOf(origin) === -1) {
        origins.push(origin)
      }
    } catch (e) {
      // Malformed config value - skip rather than break telemetry init.
    }
  })

  return origins.map((origin) => new RegExp('^' + escapeRegExp(origin)))
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Replaces query-string VALUES with '<redacted>' while keeping parameter names.
 *
 * The ui6 bundle is public and this SPA puts record/tenant ids in query
 * strings, so raw URLs must not reach Datadog. Keeping the names preserves
 * enough shape to debug without leaking the values.
 */
export function redactQueryString(url: string): string {
  const queryStart = url.indexOf('?')
  if (queryStart === -1) {
    return url
  }

  const base = url.slice(0, queryStart)
  const hashStart = url.indexOf('#', queryStart)
  const query = url.slice(queryStart + 1, hashStart === -1 ? undefined : hashStart)
  const hash = hashStart === -1 ? '' : url.slice(hashStart)

  const redacted = query
    .split('&')
    .map((pair) => {
      if (!pair) return pair
      const eq = pair.indexOf('=')
      return eq === -1 ? pair : `${pair.slice(0, eq)}=<redacted>`
    })
    .join('&')

  return `${base}?${redacted}${hash}`
}

/**
 * Strips query-string values from URL attributes and populates the attributes
 * Datadog actually reads for its "HTTP URL" column.
 *
 * Runs as the FIRST span processor so the mutation lands before
 * BatchSpanProcessor hands the span to the exporter -- both processors see the
 * same object reference, and onEnd fires in registration order.
 */
class UrlRedactionProcessor implements SpanProcessor {
  onStart(): void {
    // no-op: URLs are only known once the request completes
  }

  onEnd(span: ReadableSpan): void {
    const attributes = span.attributes as Record<string, unknown>
    const raw = attributes['http.url']
    if (typeof raw !== 'string') {
      return
    }

    const safe = redactQueryString(raw)
    attributes['http.url'] = safe // Datadog-reserved attribute
    attributes['url.full'] = safe

    try {
      attributes['url.path'] = new URL(raw, window.location.origin).pathname
    } catch (e) {
      // Non-absolute or malformed URL - leave url.path unset.
    }
  }

  shutdown(): Promise<void> {
    return Promise.resolve()
  }

  forceFlush(): Promise<void> {
    return Promise.resolve()
  }
}

/** Marks every span as browser-originated, matching redmap.request.source on the .NET services. */
function tagRequestSource(span: Span): void {
  span.setAttribute('redmap.request.source', 'browser')
}

let provider: WebTracerProvider | undefined

/**
 * Boots browser tracing. Safe to call once, early in src/index.tsx.
 *
 * No-ops unless REACT_APP_OTEL_EXPORT_ENABLED === 'true' so `yarn start` does
 * not fire spans at an endpoint that only exists inside the ECS task. Every
 * failure is swallowed: telemetry must never stop the app from booting.
 */
export default function initTelemetry(): WebTracerProvider | undefined {
  if (process.env.REACT_APP_OTEL_EXPORT_ENABLED !== 'true') {
    return undefined
  }

  if (provider) {
    return provider
  }

  try {
    const settings = resolveSettings()

    if (process.env.NODE_ENV !== 'production') {
      diag.setLogger(new DiagConsoleLogger(), DiagLogLevel.ERROR)
    }

    const sampler: Sampler =
      settings.sampleRatio >= 1.0
        ? new AlwaysOnSampler()
        : new TraceIdRatioBasedSampler(settings.sampleRatio)

    provider = new WebTracerProvider({
      resource: new Resource({
        'service.name': settings.serviceName,
        'service.version': settings.serviceVersion,
        // Both keys: Datadog maps deployment.environment, newer OTel emits
        // deployment.environment.name. Same pairing as OtelSetup.cs.
        'deployment.environment': settings.environment,
        'deployment.environment.name': settings.environment,
        team: 'redmap',
        application: settings.serviceName,
      }),
      sampler,
      spanProcessors: [
        new UrlRedactionProcessor(),
        new BatchSpanProcessor(new OTLPTraceExporter({url: settings.endpoint})),
      ],
    })

    provider.register({
      // Keeps span context across async boundaries in the browser, so XHR spans
      // nest under the interaction/document-load span that caused them.
      contextManager: new ZoneContextManager(),
      propagator: new CompositePropagator({
        propagators: [new W3CTraceContextPropagator(), new W3CBaggagePropagator()],
      }),
    })

    // The exporter's own POSTs must never be traced, or each export generates
    // the span that triggers the next export.
    const ignoreUrls = [new RegExp(escapeRegExp(TRACES_PATH)), /\/\.well-known\//]

    const propagateTraceHeaderCorsUrls = settings.propagateCors ? settings.corsUrls : []

    registerInstrumentations({
      tracerProvider: provider,
      instrumentations: [
        new DocumentLoadInstrumentation(),
        new XMLHttpRequestInstrumentation({
          // axios 0.21 uses XMLHttpRequest in the browser, so this - not the
          // fetch instrumentation - covers essentially all of the app's traffic.
          ignoreUrls,
          propagateTraceHeaderCorsUrls,
          clearTimingResources: true,
          applyCustomAttributesOnSpan: tagRequestSource,
        }),
        new FetchInstrumentation({
          ignoreUrls,
          propagateTraceHeaderCorsUrls,
          clearTimingResources: true,
          applyCustomAttributesOnSpan: tagRequestSource,
        }),
      ],
    })

    registerFlushHandlers(provider)

    // eslint-disable-next-line no-console
    console.log(
      `[OpenTelemetry] ${settings.serviceName}@${settings.serviceVersion} ` +
        `env=${settings.environment} ratio=${settings.sampleRatio} -> ${settings.endpoint}` +
        (settings.propagateCors ? ' (cors trace propagation ON)' : '')
    )

    return provider
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[OpenTelemetry] initialisation failed, continuing without tracing', e)
    return undefined
  }
}

/**
 * Flush buffered spans when the page is being backgrounded or unloaded.
 * Without this, BatchSpanProcessor drops whatever is still queued when a user
 * navigates away - which is most of a short session.
 */
function registerFlushHandlers(activeProvider: WebTracerProvider): void {
  const flush = () => {
    activeProvider.forceFlush().catch(() => {
      // Nothing useful to do if the final flush fails.
    })
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flush()
    }
  })
  window.addEventListener('pagehide', flush)
}
